// InstallPrompt — encourages mobile users to install Go2Fix as a PWA.
//
// Two flows:
//
//   1. Android / Chromium / Edge — fires `beforeinstallprompt`, we stash the
//      event, then surface a custom banner the user can click to install.
//      Clicking calls `prompt()` and we track accept/dismiss outcomes.
//
//   2. iOS Safari — no programmatic install API. We detect iOS + not-yet-
//      standalone and show an instructions sheet (Share → Add to Home Screen).
//
// Defer rules so the prompt doesn't feel pushy:
//   - Wait at least 30s after first paint (15s if user came from a city ×
//     service landing page, where intent is high — `?source=*` or `/servicii/*`)
//   - Wait until viewport is mobile (no install banners on desktop)
//   - Skip if already standalone
//   - Skip on mid-flow routes (login, register, booking wizard)
//   - 3-day cooldown after each "Mai târziu"
//   - After 3 dismissals total, the next prompt opens the explainer sheet
//     (richer copy) and on dismiss applies a 90-day cooldown
//   - Skip if user has already installed (`appinstalled` event)
//
// Per-role copy variation: when the user is authenticated, the banner subtitle
// adapts to their role (worker vs company vs client). Admins are suppressed —
// they live on desktop.

import { useEffect, useState, useCallback, useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import { useAppMode } from '@/hooks/useAppMode'
import { useAuth } from '@/context/AuthContext'
import { trackEvent } from '@/lib/analytics'

const DEFER_KEY = 'go2fix:pwa-install-deferred-at'
const DISMISS_COUNT_KEY = 'go2fix:pwa-install-dismiss-count'
const LONG_COOLDOWN_KEY = 'go2fix:pwa-install-long-cooldown-at'
const DEFER_COOLDOWN_MS = 3 * 24 * 60 * 60 * 1000 // 3 days
const LONG_COOLDOWN_MS = 90 * 24 * 60 * 60 * 1000 // 90 days
const DISMISS_THRESHOLD = 3
const SHOW_AFTER_MS_DEFAULT = 30 * 1000
const SHOW_AFTER_MS_HIGH_INTENT = 15 * 1000

// Routes where the prompt must never appear (mid-flow).
const SUPPRESS_PATHS = [
  /^\/autentificare/,
  /^\/inregistrare/,
  /^\/login/,
  /^\/register/,
  /^\/rezervare/,
  /^\/booking/,
  /^\/onboarding/,
]

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>
}

type Role = 'CLIENT' | 'WORKER' | 'COMPANY' | 'ADMIN' | 'GLOBAL_ADMIN' | null

interface Copy {
  title: string
  subtitle: string
}

function copyForRole(role: Role): Copy {
  switch (role) {
    case 'WORKER':
      return {
        title: 'Instalează Go2Fix',
        subtitle: 'Notificări instant pentru joburi noi.',
      }
    case 'COMPANY':
      return {
        title: 'Instalează Go2Fix',
        subtitle: 'Gestionează echipa și comenzile direct de pe telefon.',
      }
    case 'CLIENT':
    default:
      return {
        title: 'Instalează Go2Fix',
        subtitle: 'Acces rapid, ca o aplicație nativă.',
      }
  }
}

function readNumber(key: string): number {
  try {
    return Number(localStorage.getItem(key) || '0')
  } catch {
    return 0
  }
}

function writeNumber(key: string, value: number) {
  try {
    localStorage.setItem(key, String(value))
  } catch {
    /* private mode */
  }
}

export default function InstallPrompt() {
  const mode = useAppMode()
  const { user } = useAuth()
  const location = useLocation()
  const [deferredEvent, setDeferredEvent] = useState<BeforeInstallPromptEvent | null>(null)
  const [show, setShow] = useState(false)
  const [showIOSSheet, setShowIOSSheet] = useState(false)
  const [showExplainer, setShowExplainer] = useState(false)

  const role = (user?.role ?? null) as Role
  const suppressForAdmin = role === 'ADMIN' || role === 'GLOBAL_ADMIN'
  const copy = useMemo(() => copyForRole(role), [role])

  const isSuppressedRoute = useMemo(
    () => SUPPRESS_PATHS.some((re) => re.test(location.pathname)),
    [location.pathname],
  )

  // High-intent: came via a campaign source param or city × service page.
  const isHighIntent = useMemo(() => {
    const search = new URLSearchParams(location.search)
    if (search.get('source')) return true
    return /^\/servicii\//.test(location.pathname)
  }, [location.pathname, location.search])

  // Capture the install event from Chromium on mobile.
  useEffect(() => {
    const onBeforeInstall = (e: Event) => {
      e.preventDefault()
      setDeferredEvent(e as BeforeInstallPromptEvent)
    }
    const onInstalled = () => {
      setDeferredEvent(null)
      setShow(false)
      setShowIOSSheet(false)
      setShowExplainer(false)
      trackEvent('pwa_installed')
    }
    window.addEventListener('beforeinstallprompt', onBeforeInstall)
    window.addEventListener('appinstalled', onInstalled)
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall)
      window.removeEventListener('appinstalled', onInstalled)
    }
  }, [])

  // Decide if/when to show after engagement timer.
  useEffect(() => {
    if (mode.isStandalone) return
    if (!mode.isMobileViewport) return
    if (suppressForAdmin) return
    if (isSuppressedRoute) return

    // Long cooldown takes precedence (set after 3 dismissals).
    const longAt = readNumber(LONG_COOLDOWN_KEY)
    if (longAt && Date.now() - longAt < LONG_COOLDOWN_MS) return

    // Standard 3-day cooldown.
    const deferredAt = readNumber(DEFER_KEY)
    if (deferredAt && Date.now() - deferredAt < DEFER_COOLDOWN_MS) return

    const showAfter = isHighIntent ? SHOW_AFTER_MS_HIGH_INTENT : SHOW_AFTER_MS_DEFAULT
    const dismissCount = readNumber(DISMISS_COUNT_KEY)

    const timeout = window.setTimeout(() => {
      // Past the threshold? Show the explainer sheet instead of the slim banner.
      if (dismissCount >= DISMISS_THRESHOLD) {
        setShowExplainer(true)
        trackEvent('pwa_install_prompted', {
          platform: mode.isIOS ? 'ios' : 'android',
          variant: 'explainer',
        })
        return
      }

      if (mode.isIOS && !mode.isStandalone) {
        setShowIOSSheet(true)
        trackEvent('pwa_install_prompted', { platform: 'ios', variant: 'banner' })
      } else if (deferredEvent) {
        setShow(true)
        trackEvent('pwa_install_prompted', { platform: 'android', variant: 'banner' })
      }
      // If neither (desktop, non-iOS without beforeinstallprompt yet) — silent.
    }, showAfter)

    return () => window.clearTimeout(timeout)
  }, [
    mode.isStandalone,
    mode.isMobileViewport,
    mode.isIOS,
    deferredEvent,
    suppressForAdmin,
    isSuppressedRoute,
    isHighIntent,
  ])

  const handleInstall = useCallback(async () => {
    if (!deferredEvent) return
    try {
      await deferredEvent.prompt()
      const choice = await deferredEvent.userChoice
      trackEvent(
        choice.outcome === 'accepted' ? 'pwa_install_accepted' : 'pwa_install_dismissed',
        { platform: 'android' },
      )
      setDeferredEvent(null)
      setShow(false)
      setShowExplainer(false)
    } catch {
      setShow(false)
      setShowExplainer(false)
    }
  }, [deferredEvent])

  const handleDefer = useCallback((platform: 'android' | 'ios', from: 'banner' | 'explainer' = 'banner') => {
    const newCount = readNumber(DISMISS_COUNT_KEY) + 1
    writeNumber(DISMISS_COUNT_KEY, newCount)
    writeNumber(DEFER_KEY, Date.now())
    if (from === 'explainer' || newCount >= DISMISS_THRESHOLD) {
      writeNumber(LONG_COOLDOWN_KEY, Date.now())
    }
    trackEvent('pwa_install_dismissed', { platform, from, count: newCount })
    setShow(false)
    setShowIOSSheet(false)
    setShowExplainer(false)
  }, [])

  // ── Explainer sheet (after 3 dismissals — richer copy) ──────────────────────
  if (showExplainer) {
    return (
      <div
        role="dialog"
        aria-label="Instalează aplicația Go2Fix"
        className="fixed inset-0 z-[60] flex items-end md:hidden"
      >
        <button
          aria-label="Închide"
          className="absolute inset-0 bg-black/40"
          onClick={() => handleDefer(mode.isIOS ? 'ios' : 'android', 'explainer')}
        />
        <div className="relative w-full bg-white rounded-t-2xl safe-bottom safe-x px-5 pt-3 pb-6 animate-[fadeUp_0.3s_ease-out]">
          <div className="mx-auto w-10 h-1 bg-gray-300 rounded-full mb-4" aria-hidden="true" />
          <div className="flex items-center gap-3 mb-4">
            <img src="/icon.png" alt="" aria-hidden="true" className="w-12 h-12 rounded-xl" />
            <div>
              <p className="text-base font-semibold text-gray-900">{copy.title}</p>
              <p className="text-sm text-gray-500">De ce să o instalezi?</p>
            </div>
          </div>
          <ul className="text-sm text-gray-700 space-y-2.5 mb-5">
            <li className="flex items-start gap-2">
              <span className="text-blue-600 font-bold leading-tight">·</span>
              <span>Mai rapidă — pornește instant, fără adresă.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-blue-600 font-bold leading-tight">·</span>
              <span>Notificări push pentru actualizări la comenzi.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-blue-600 font-bold leading-tight">·</span>
              <span>Funcționează parțial și fără internet.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-blue-600 font-bold leading-tight">·</span>
              <span>Fără App Store — direct din browser.</span>
            </li>
          </ul>
          {mode.isIOS ? (
            <div className="text-xs text-gray-500 mb-4">
              În Safari: <strong>Share</strong> → <strong>„Adaugă la ecranul principal"</strong>.
            </div>
          ) : null}
          <div className="flex flex-col gap-2">
            {!mode.isIOS && deferredEvent ? (
              <button
                onClick={handleInstall}
                className="w-full bg-blue-600 active:bg-blue-700 active:scale-[0.99] transition text-white text-sm font-medium px-4 py-3 rounded-xl"
              >
                Instalează acum
              </button>
            ) : null}
            <button
              onClick={() => handleDefer(mode.isIOS ? 'ios' : 'android', 'explainer')}
              className="w-full text-gray-500 text-sm px-4 py-3"
            >
              Nu mulțumesc
            </button>
          </div>
        </div>
      </div>
    )
  }

  // ── Android banner (slim, bottom sheet style) ───────────────────────────────
  if (show && deferredEvent) {
    return (
      <div
        role="dialog"
        aria-label={copy.title}
        className="fixed bottom-0 left-0 right-0 z-[60] safe-bottom safe-x p-3 md:hidden"
      >
        <div className="mx-auto max-w-md bg-white rounded-2xl shadow-2xl border border-gray-200 p-4 flex items-center gap-3 animate-[fadeUp_0.3s_ease-out]">
          <img src="/icon.png" alt="" aria-hidden="true" className="w-12 h-12 rounded-xl flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-gray-900 leading-tight">{copy.title}</p>
            <p className="text-xs text-gray-500 mt-0.5 leading-snug">{copy.subtitle}</p>
          </div>
          <div className="flex flex-col gap-1.5 flex-shrink-0">
            <button
              onClick={handleInstall}
              className="bg-blue-600 active:bg-blue-700 active:scale-[0.97] transition text-white text-sm font-medium px-4 py-2 rounded-xl"
            >
              Instalează
            </button>
            <button
              onClick={() => handleDefer('android')}
              className="text-gray-500 text-xs px-2 py-1"
            >
              Mai târziu
            </button>
          </div>
        </div>
      </div>
    )
  }

  // ── iOS instructions sheet ────────────────────────────────────────────────
  if (showIOSSheet) {
    return (
      <div
        role="dialog"
        aria-label="Adaugă Go2Fix pe ecranul principal"
        className="fixed inset-0 z-[60] flex items-end md:hidden"
      >
        <button
          aria-label="Închide"
          className="absolute inset-0 bg-black/40"
          onClick={() => handleDefer('ios')}
        />
        <div className="relative w-full bg-white rounded-t-2xl safe-bottom safe-x px-5 pt-3 pb-6 animate-[fadeUp_0.3s_ease-out]">
          <div className="mx-auto w-10 h-1 bg-gray-300 rounded-full mb-4" aria-hidden="true" />
          <div className="flex items-center gap-3 mb-4">
            <img src="/icon.png" alt="" aria-hidden="true" className="w-12 h-12 rounded-xl" />
            <div>
              <p className="text-base font-semibold text-gray-900">Adaugă Go2Fix pe ecranul principal</p>
              <p className="text-sm text-gray-500">{copy.subtitle}</p>
            </div>
          </div>
          <ol className="text-sm text-gray-700 space-y-3 mb-5">
            <li className="flex items-start gap-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 text-blue-700 text-xs font-semibold flex items-center justify-center">1</span>
              <span>
                Apasă pe pictograma de partajare{' '}
                <span aria-hidden="true" className="inline-flex items-center justify-center w-6 h-6 rounded bg-gray-100 align-middle">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
                    <polyline points="16 6 12 2 8 6" />
                    <line x1="12" y1="2" x2="12" y2="15" />
                  </svg>
                </span>{' '}
                din bara de jos.
              </span>
            </li>
            <li className="flex items-start gap-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 text-blue-700 text-xs font-semibold flex items-center justify-center">2</span>
              <span>Selectează <strong>„Adaugă la ecranul principal"</strong>.</span>
            </li>
          </ol>
          <button
            onClick={() => handleDefer('ios')}
            className="w-full bg-blue-600 active:bg-blue-700 active:scale-[0.99] transition text-white text-sm font-medium px-4 py-3 rounded-xl"
          >
            Am înțeles
          </button>
        </div>
      </div>
    )
  }

  return null
}
